import { StructureComponent } from '../../../views/site/structure/structure.component';
import { DrawerComponentLiaisonBase } from '../drawers/drawer-base-classes';
import { IListItemManager } from '../../interfaces/ilist-item.interface';
import { IDrawerComponentLiaison } from '../../interfaces/idrawer-component-liaison';
import { FormInstance } from '../site-content/form-instance.model';
import { Folder } from '../site-content/folder.model';
import { UtilityHelper } from '../../utility.helper';
import { FolderDetailsComponent } from '../../components/folder-details/folder-details.component';
import { FormInstanceDetailsComponent } from '../../components/form-instance-details/form-instance-details.component';

export class FolderPropertiesDrawerComponentLiaison extends DrawerComponentLiaisonBase implements IDrawerComponentLiaison {
    public constructor(private structureComponent: StructureComponent, private originalFolder: Folder) {
        super();

        this.initializationHasCompleted();
    }

    // Implement IDrawerComponentLiaison methods.
    public get HandlesDrawerComponentTag(): string {
        return 'app-folder-details';
    }

    // This method gets called from the ngOnInit() method of all derivatives of DrawerComponentBase
    // it's the place for a DrawerComponentLiaison to configure the supplied drawer component with
    // whatever it needs -- in this case it sets some properties of the FolderDetailsComponent passed to it
    // and subscribes to and handles it events
    public drawerComponentInitialized(drawerComponentParam: any): void {
        let drawerComponent: FolderDetailsComponent = <FolderDetailsComponent>drawerComponentParam;

        // Set the drawer's input value(s).
        drawerComponent.folder = this.originalFolder;
        drawerComponent.mode = this.structureComponent.DetailsMode;

        // Listen for the component's outputs.
        //(save) = "this.saveFolder($event)"
        drawerComponent.save.subscribe(updatedFolder => {
            this.saveFolder(updatedFolder);
        });

        // (workflowStateUpdate) = "this.updateWorkflowState($event)"
        drawerComponent.workflowStateUpdate.subscribe(updatedFolder => {
            this.updateWorkflowState(updatedFolder);
        });

        // (delete) = "this.deleteEntity($event)"
        drawerComponent.delete.subscribe(folderToDelete => {
            this.deleteFolder(folderToDelete);
        })

        //(close) = "this.deselectSelected()"
        drawerComponent.close.subscribe(naEvent => {
            this.structureComponent.deselectSelected();

            this.structureComponent.DrawerComponentLiaison = null;
        });
    }
    public drawerComponentDestroyed(drawerComponentParam: any): void {
        // NOTE:  THIS METHOD IS A NOOP BY DESIGN.
    }

    // Helper methods.
    private saveFolder(folder: Folder): void {
        this.structureComponent.TreeIsStale = false;
        this.structureComponent.FolderService.update(folder).then(response => {
            this.structureComponent.SelectedFolder = response;
            this.structureComponent.reloadCurrentFolder();
            IListItemManager.findAndUpdateFrom(this.structureComponent.ListItems, this.structureComponent.SelectedFolder);
            UtilityHelper.findAndAssignFrom(this.structureComponent.CurrentFolder.childFolders, this.structureComponent.SelectedFolder);
            this.structureComponent.forceTreeRedraw();
            this.structureComponent.deselectSelected();
            this.structureComponent.DrawerComponentLiaison = null;
        });
    }

    private updateWorkflowState(folder: Folder): void {
        this.structureComponent.TreeIsStale = false;
        this.structureComponent.FolderService.transitionFolder(folder).then(response => {
            this.structureComponent.SelectedFolder = response;
            IListItemManager.findAndUpdateFrom(this.structureComponent.ListItems, this.structureComponent.SelectedFolder);
            UtilityHelper.findAndAssignFrom(this.structureComponent.CurrentFolder.childFolders, this.structureComponent.SelectedFolder);
            this.structureComponent.forceTreeRedraw();
            this.structureComponent.deselectSelected();
        });
    }

    private deleteFolder(folder: Folder): void {
        this.structureComponent.deleteOnConfirm("Folder", () => {
            let url: string = `/site/${this.structureComponent.site.id}/structure/${folder.parentFolderId}`;
            this.structureComponent.FolderService.delete(folder.id).then(x => {
                let newList = this.structureComponent.ListItems.filter(item => {
                    return item.getId() !== folder.id;
                });
                this.structureComponent.ListItems = newList;
                this.structureComponent.SelectedFolder = null;

                // In case the folder tree is
                // being shown, force it to redraw.
                this.structureComponent.forceTreeRedraw();
            });
        });
    }

}
export class FormInstancePropertiesDrawerComponentLiaison extends DrawerComponentLiaisonBase implements IDrawerComponentLiaison {
    public constructor(private structureComponent: StructureComponent, private originalFormInstance: FormInstance) {
        super();

        this.initializationHasCompleted();
    }

    // Implement IDrawerComponentLiaison methods.
    public get HandlesDrawerComponentTag(): string {
        return 'app-form-instance-details';
    }

    public drawerComponentInitialized(drawerComponentParam: any): void {
        let drawerComponent: FormInstanceDetailsComponent = <FormInstanceDetailsComponent>drawerComponentParam;

        // Set the drawer's input value(s).
        drawerComponent.formInstance = this.originalFormInstance;

        // Listen for the component's outputs.
        // save()
        drawerComponent.save.subscribe(updatedFormInstance => {
            this.saveFormInstance(updatedFormInstance);

            this.structureComponent.DrawerComponentLiaison = null;
        });

        // transition()
        drawerComponent.transition.subscribe(formInstance => {
            this.transitionFormInstance(formInstance);
        });

        // delete()
        drawerComponent.delete.subscribe(formInstance => {
            this.deleteFormInstance(formInstance);
        });

        // close()
        drawerComponent.close.subscribe(naEvent => {
            this.structureComponent.deselectSelected();

            this.structureComponent.DrawerComponentLiaison = null;
        });
    }
    public drawerComponentDestroyed(drawerComponentParam: any): void {
        // NOTE:  THIS METHOD IS A NOOP BY DESIGN.
    }

    // Helper methods.
    private saveFormInstance(updatedFormInstance: FormInstance): void {
        this.structureComponent.FormInstanceService.save(updatedFormInstance).then(response => {
            this.structureComponent.SelectedFormInstance = response;
            IListItemManager.findAndUpdateFrom(this.structureComponent.ListItems, this.structureComponent.SelectedFormInstance);
            this.structureComponent.deselectSelected();
        });
    }

    private transitionFormInstance(formInstance: FormInstance): void {
        this.structureComponent.FormInstanceService.transitionWorkflowState(formInstance).then(response => {
            this.structureComponent.SelectedFormInstance = response;
            IListItemManager.findAndUpdateFrom(this.structureComponent.ListItems, this.structureComponent.SelectedFormInstance);
            this.structureComponent.deselectSelected();
        });
    }

    private deleteFormInstance(formInstance: FormInstance): void {
        let url: string = this.structureComponent.returnPath(); // `/site/${this.site.id}/structure/folder/${formInstance.folderId}`;
        this.structureComponent.Router.navigateByUrl(url).then(x => {
            this.structureComponent.deleteOnConfirm("Form Instance", () => {
                this.structureComponent.FormInstanceService.delete(formInstance.id).then(x => {
                    let formId = formInstance.id; //form.referencedFormInstance == null ? form.id : form.referencedFormInstance.id;
                    let newList = this.structureComponent.ListItems.filter(item => {
                        return item.getId() !== formId;
                    });
                    this.structureComponent.ListItems = newList;
                    this.structureComponent.SelectedFormInstance = null;

                    // In case the folder tree is
                    // being shown, force it to redraw.
                    this.structureComponent.forceTreeRedraw();
                });
            })
        });
    }
}
